///RequisitionRequestItemGroupViewBase

import { Box, Button, Checkbox, FormControlLabel, Icon, IconButton, InputAdornment, Typography } from "@material-ui/core";
import { Cell, Grid } from "@shoothill/core";
import { pxToRem } from "Globals/Styles/AppTheme";
import { useObserver } from "mobx-react-lite";
import React from "react";
import styled from "styled-components";

import DeleteLogo from "Content/Bin.svg";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { DarwinInput, DarwinMoneyInput } from "Globals/Styles/Control/DarwinInput";
import { DashedDivider, H2TextSolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { RequisitionRequestItemGroupViewModel } from "./RequisitionRequestItemGroupViewModel";
import { RequisitionRequestItemGroupModel } from "./RequisitionRequestItemGroupModel";
import { RequisitionRequestItemModel } from "./RequisitionRequestItemModel";
import { BudgetForecastTable } from "./BudgetForecastTable";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { PurchaseOrderViewModel } from "Views/PurchaseOrder/Form/PurchaseOrderViewModel";
import { IOrderLineDto, OrderLineModel } from "../Form/OrderLineModel";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";

interface IProps {
    className?: string;
    viewModel: RequisitionRequestItemGroupViewModel;
    isDisabled: boolean;
    canShowBudgetForecast: boolean;
    isCentral: boolean;
    poViewModel: PurchaseOrderViewModel;
    isStock: boolean;
}

const RequisitionRequestItemGroupViewBase: React.FC<IProps> = (props) => {
    // #region Code Behind

    const COLUMNS8 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

    const addItem = () => {
        if (!props.isStock) {
            let itemViewModel = props.viewModel;

            let dto: IOrderLineDto = {
                materialId: null,
                categoryId: itemViewModel.model.categoryId,
                categoryDisplayName: itemViewModel.model.categoryDisplayName,
                subCategoryId: itemViewModel.model.subCategoryId,
                subCategoryDisplayName: itemViewModel.model.subCategoryDisplayName,
                lineDescriptionId: itemViewModel.model.lineDescriptionId,
                variationId: itemViewModel.model.variationId,
                lineDescriptionDisplayName: itemViewModel.model.lineDescriptionDisplayName,
                itemDescription: "",
                units: 0.0,
                unitPrice: 0.0,
                dateFrom: null,
                dateTo: null,
                isNA: false,
            };

            let model: OrderLineModel = new OrderLineModel();

            model.fromDto(dto);

            props.poViewModel.addOrderLineToRequisitionGroupAsync(model);
        }
    };

    // #endregion Code Behind

    return useObserver(() => (
        <Box className={props.className} key={props.viewModel.key} id={props.viewModel.model.id ? `group-${props.viewModel.model.id}` : ""}>
            <GroupHeader>
                <Typography variant="h2">{props.viewModel.displayName}</Typography>
                {props.isStock && <div></div>}
                {!props.isStock && <PrimaryButton displayName="Add" execute={addItem} fullWidth={true} canExecute={!props.isDisabled} />}
            </GroupHeader>
            <H2TextSolidDivider />
            <GroupTable>
                {props.viewModel.requisitionRequestItems.map((ri, index) => (
                    <>
                        <GroupRow key={`order-line-row-${index}`}>
                            <Grid columnGap={30} rowGap={0} tc={COLUMNS8} dc={COLUMNS8} alignItems={"center"}>
                                {/* Group of 8 columns */}
                                <Cell ts={4} ds={4}>
                                    <DarwinInput<RequisitionRequestItemModel>
                                        type="text"
                                        label=""
                                        placeholder="Description"
                                        validateOnBlur={true}
                                        viewModel={ri}
                                        fieldName="itemDescription"
                                        shrink={true}
                                        editMode={!props.isDisabled && !ri.isStockOrderLine}
                                    />
                                </Cell>
                                <Cell ts={1} ds={1}>
                                    {(() => {
                                        switch (true) {
                                            case !props.isDisabled && !ri.isStockOrderLine:
                                                return (
                                                    <DarwinInput<RequisitionRequestItemModel>
                                                        type="number"
                                                        label=""
                                                        placeholder=""
                                                        validateOnBlur={true}
                                                        viewModel={ri}
                                                        fieldName="units"
                                                        shrink={true}
                                                        maxLength={32}
                                                        editMode={!props.isDisabled}
                                                        onChange={props.viewModel.handleLineChange}
                                                    />
                                                );

                                            case !props.isDisabled && ri.isStockOrderLine:
                                                return (
                                                    <GroupCell>
                                                        <PrimaryButton
                                                            canExecute={ri.canVoidItemUnits}
                                                            displayName="Void"
                                                            execute={ri.apiVoidItemUnits}
                                                            style={{ marginRight: "15px" }}
                                                        />
                                                        {ri.model.units}
                                                    </GroupCell>
                                                );

                                            default:
                                                return <GroupCell>{ri.model.units}</GroupCell>;
                                        }
                                    })()}
                                </Cell>
                                <Cell ts={1} ds={1}>
                                    {(() => {
                                        switch (true) {
                                            case !props.isDisabled && !ri.isStockOrderLine:
                                                return (
                                                    <DarwinMoneyInput<RequisitionRequestItemModel>
                                                        type="number"
                                                        label=""
                                                        placeholder=""
                                                        validateOnBlur={true}
                                                        viewModel={ri}
                                                        fieldName="unitPrice"
                                                        shrink={true}
                                                        maxLength={11}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                                        }}
                                                        editMode={!props.isDisabled && !ri.isStockOrderLine}
                                                        onChange={props.viewModel.handleLineChange}
                                                    />
                                                );

                                            case props.isDisabled || ri.isStockOrderLine:
                                                return <GroupCell>{ri.unitPrice}</GroupCell>;
                                        }
                                    })()}
                                </Cell>
                                <Cell ts={1} ds={1}>
                                    <GroupCell>{ri.total}</GroupCell>
                                </Cell>
                                <Cell ts={1} ds={1}>
                                    {ri.canDelete && (
                                        <ButtonGroupCell>
                                            <IconButton
                                                onClick={() => {
                                                    ri.setDeleted();
                                                    props.viewModel.handleLineChange();
                                                    props.viewModel.handleLineDelete();
                                                }}
                                                disabled={props.isDisabled || !ri.canDelete}
                                            >
                                                <Icon>
                                                    <img alt="edit" src={DeleteLogo} />
                                                </Icon>
                                            </IconButton>
                                        </ButtonGroupCell>
                                    )}
                                </Cell>

                                {/* Group of 8 columns */}
                                {/* <Cell ts={8} ds={8}>
                                    <DashedDivider />
                                </Cell> */}
                            </Grid>
                            {props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && (
                                <Grid columnGap={30} rowGap={0} tc={COLUMNS8} dc={COLUMNS8} alignItems={"center"}>
                                    {/* Group of 8 columns */}
                                    <Cell ts={2} ds={2}>
                                        {(() => {
                                            switch (true) {
                                                case !props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy:
                                                    return (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={ri.model.isNA}
                                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => ri.setValue("isNA", checked)}
                                                                    disabled={props.poViewModel.IsLoading}
                                                                />
                                                            }
                                                            label={"Hire period N/A"}
                                                            name={`is-na-checkbox`}
                                                            disabled={props.poViewModel.IsLoading}
                                                            labelPlacement={"top"}
                                                            style={{ marginBottom: "-10px" }}
                                                        />
                                                    );

                                                case props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy:
                                                    return <GroupCell>{ri.isNAWithDatesFormatted}</GroupCell>;

                                                default:
                                                    return <GroupCell></GroupCell>;
                                            }
                                        })()}
                                    </Cell>

                                    <Cell ts={1} ds={1}>
                                        {(() => {
                                            switch (true) {
                                                case !props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && !ri.model.isNA:
                                                    return (
                                                        <DarwinDateSelect
                                                            displayName=""
                                                            execute={(value: string | null) => ri.setValue("dateFrom", value)}
                                                            onBlur={() => ri.isFieldValid("dateFrom")}
                                                            placeholder="Please select"
                                                            validationMessage={ri.getError("dateFrom")}
                                                            value={ri.getValue("dateFrom")}
                                                            //canExecute={ri.canExecuteHireOrderDateField}
                                                        />
                                                    );

                                                case props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && !ri.model.isNA:
                                                    return <GroupCell></GroupCell>; // Shows in isNAWithDatesFormatted

                                                default:
                                                    return <GroupCell></GroupCell>;
                                            }
                                        })()}
                                    </Cell>
                                    <Cell ts={1} ds={1}>
                                        {(() => {
                                            switch (true) {
                                                case !props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && !ri.model.isNA:
                                                    return (
                                                        <DarwinDateSelect
                                                            displayName=""
                                                            execute={(value: string | null) => ri.setValue("dateTo", value)}
                                                            onBlur={() => ri.isFieldValid("dateTo")}
                                                            placeholder="Please select"
                                                            validationMessage={ri.getError("dateTo")}
                                                            value={ri.getValue("dateTo")}
                                                            //canExecute={ri.canExecuteHireOrderDateField}
                                                        />
                                                    );

                                                case props.isDisabled && props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && !ri.model.isNA:
                                                    return <GroupCell></GroupCell>; // Shows in isNAWithDatesFormatted

                                                default:
                                                    return <GroupCell></GroupCell>;
                                            }
                                        })()}
                                    </Cell>

                                    <Cell ts={4} ds={4}></Cell>

                                    {/* Group of 8 columns */}
                                    {/* <Cell ts={8} ds={8}>
                                        <DashedDivider />
                                    </Cell> */}
                                </Grid>
                            )}

                            <SnackBar
                                messageText={ri.snackMessage}
                                messageType={ri.snackType}
                                active={ri.snackbarState}
                                closeOption={() => ri.setSnackbarState(false)}
                                autoHideDuration={5000}
                            />
                        </GroupRow>
                        <DashedDivider gutterTop gutterSize={10} />
                    </>
                ))}
                <Grid columnGap={30} rowGap={0} tc={COLUMNS8} dc={COLUMNS8}>
                    {/* Group of 8 columns */}
                    <Cell ts={8} ds={8}>
                        <GroupTotal>Sub-Total: {props.viewModel.total}</GroupTotal>
                    </Cell>
                </Grid>
            </GroupTable>
            {!props.isCentral && (
                <NewFutureSpendSection>
                    <Grid columnGap={30} rowGap={20} tc={COLUMNS8} dc={COLUMNS8}>
                        {/* Group of 8 columns */}
                        <Cell ts={1} ds={1}>
                            <NewFutureSpendLabel>Suggested new future spend</NewFutureSpendLabel>
                        </Cell>
                        <Cell ts={1} ds={1} style={{ display: props.isDisabled ? "flex" : "block", alignItems: props.isDisabled ? "center" : "initial" }}>
                            <DarwinMoneyInput<RequisitionRequestItemGroupModel>
                                type="number"
                                label=""
                                placeholder=""
                                validateOnBlur={true}
                                viewModel={props.viewModel}
                                fieldName="newFutureSpend"
                                shrink={true}
                                maxLength={32}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                }}
                                editMode={!props.isDisabled}
                            />
                        </Cell>
                        <Cell ts={6} ds={6} style={{ display: props.isDisabled ? "flex" : "block", alignItems: props.isDisabled ? "center" : "initial" }}>
                            <DarwinInput<RequisitionRequestItemGroupModel>
                                type="text"
                                label=""
                                placeholder="Notes"
                                validateOnBlur={true}
                                viewModel={props.viewModel}
                                fieldName="note"
                                shrink={true}
                                maxLength={256}
                                editMode={!props.isDisabled}
                            />
                        </Cell>
                        <Cell ts={1} ds={1}></Cell>
                    </Grid>
                </NewFutureSpendSection>
            )}

            {props.canShowBudgetForecast && <BudgetForecastTable budgetForecast={props.viewModel.getBudgetForecastTotalFormatted} isTotal={false} />}
        </Box>
    ));
};

export const RequisitionRequestItemGroupView = styled(RequisitionRequestItemGroupViewBase)`
    background-color: #ededed;
    display: flex;
    flex-direction: column;
`;

// #region Group Table

const GroupHeader = styled(Box)`
    margin: ${pxToRem(20)} ${pxToRem(16)} ${pxToRem(2)} ${pxToRem(16)};

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    > button {
        max-width: 100px;
    }

    > h2 {
        margin-right: 10px;
    }
`;

const GroupTable = styled(Box)`
    margin: ${pxToRem(0)} ${pxToRem(16)} ${pxToRem(16)} ${pxToRem(16)};
`;

const GroupRow = styled.div`
    .MuiFormHelperText-root.Mui-error {
        display: none; // Hide the error message to prevent the styling from breaking.
    }
`;

const GroupCell = styled(Box)`
    font-size: ${pxToRem(12)};
    padding: ${pxToRem(16)} 0;
`;

const ButtonGroupCell = styled(Box)`
    display: flex;
    justify-content: end;
`;

const GroupTotal = styled(Box)`
    background-color: #ced4da;
    display: flex;
    font-size: ${pxToRem(14)};
    font-weight: bold;
    justify-content: end;
    margin-top: ${pxToRem(-1)};
    padding: ${pxToRem(4)} ${pxToRem(16)};
`;

// #endregion Group Table

// #region New Future Spend

const NewFutureSpendSection = styled(Box)`
    margin: ${pxToRem(24)} ${pxToRem(16)} ${pxToRem(0)} ${pxToRem(16)};
`;

const NewFutureSpendLabel = styled(Box)`
    font-size: ${pxToRem(12)};
    font-weight: bold;
    padding: ${pxToRem(5.6)} 0;
`;

// #endregion New Future Spend
